/**
 * Time Constants
 * 
 * Constantes de tiempo para evitar magic numbers en el código
 * Todas las conversiones están centralizadas aquí para facilitar
 * el mantenimiento y la comprensión del código.
 */

/**
 * Constantes básicas de tiempo
 */
export const TIME_CONSTANTS = {
  // Unidades básicas
  MILLISECONDS_PER_SECOND: 1000,
  SECONDS_PER_MINUTE: 60,
  MINUTES_PER_HOUR: 60,
  HOURS_PER_DAY: 24,
  DAYS_PER_WEEK: 7,
  
  // Conversiones a milisegundos
  MILLISECONDS_PER_MINUTE: 60 * 1000,
  MILLISECONDS_PER_HOUR: 60 * 60 * 1000,
  MILLISECONDS_PER_DAY: 24 * 60 * 60 * 1000,
  MILLISECONDS_PER_WEEK: 7 * 24 * 60 * 60 * 1000,
  
  // Conversiones a segundos
  SECONDS_PER_HOUR: 60 * 60,
  SECONDS_PER_DAY: 24 * 60 * 60,
  SECONDS_PER_WEEK: 7 * 24 * 60 * 60,
} as const;

/**
 * Delays para la UI (en milisegundos)
 * Documentados para explicar el propósito de cada delay
 */
export const UI_DELAYS = {
  /**
   * Tiempo de buffer para asegurar que los datos estén completamente cargados
   * antes de actualizar la UI. Útil cuando hay múltiples llamadas asíncronas.
   */
  DATA_LOAD_BUFFER: 1500,
  
  /**
   * Tiempo para transiciones suaves de alertas y notificaciones
   */
  ALERT_CLOSE_DELAY: 500,
  
  /**
   * Tiempo de debounce para búsquedas y filtros
   */
  SEARCH_DEBOUNCE: 300,
  
  /**
   * Tiempo de refresco automático de datos (2 minutos)
   */
  AUTO_REFRESH_INTERVAL: 120000,
  
  /**
   * Tiempo breve para animaciones rápidas
   */
  ANIMATION_FAST: 200,
  
  /**
   * Tiempo medio para animaciones normales
   */
  ANIMATION_NORMAL: 300,
  
  /**
   * Tiempo largo para animaciones lentas
   */
  ANIMATION_SLOW: 500,
} as const;

/**
 * Intervalos de polling (en milisegundos)
 */
export const POLLING_INTERVALS = {
  /**
   * Polling muy rápido (5 segundos) - Usar con precaución
   */
  VERY_FAST: 5000,
  
  /**
   * Polling rápido (10 segundos)
   */
  FAST: 10000,
  
  /**
   * Polling normal (30 segundos)
   */
  NORMAL: 30000,
  
  /**
   * Polling lento (1 minuto)
   */
  SLOW: 60000,
  
  /**
   * Polling muy lento (5 minutos)
   */
  VERY_SLOW: 300000,
} as const;

/**
 * Helper functions para conversión de tiempo
 */
export class TimeUtils {
  /**
   * Convierte días a milisegundos
   */
  static daysToMilliseconds(days: number): number {
    return days * TIME_CONSTANTS.MILLISECONDS_PER_DAY;
  }
  
  /**
   * Convierte horas a milisegundos
   */
  static hoursToMilliseconds(hours: number): number {
    return hours * TIME_CONSTANTS.MILLISECONDS_PER_HOUR;
  }
  
  /**
   * Convierte minutos a milisegundos
   */
  static minutesToMilliseconds(minutes: number): number {
    return minutes * TIME_CONSTANTS.MILLISECONDS_PER_MINUTE;
  }
  
  /**
   * Convierte segundos a milisegundos
   */
  static secondsToMilliseconds(seconds: number): number {
    return seconds * TIME_CONSTANTS.MILLISECONDS_PER_SECOND;
  }
  
  /**
   * Obtiene una fecha futura sumando días
   */
  static addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + this.daysToMilliseconds(days));
  }
  
  /**
   * Obtiene una fecha futura sumando horas
   */
  static addHours(date: Date, hours: number): Date {
    return new Date(date.getTime() + this.hoursToMilliseconds(hours));
  }
}
